import { CalendarFilled } from '@ant-design/icons'
import { DatePicker, Input, message, Select, Spin, Typography } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useRecoilState } from 'recoil'
import {
  completeStandaloneFastlane,
  createStandaloneFastlaneTask,
  deleteStandaloneFastlaneTask,
  getStandaloneFastlaneTask,
  incompleteStandaloneFastlane,
  updateStandaloneFastlaneTask,
} from '../../../api/standalone.api'
import AopsCommonModalLayout from '../../../components/apos/modal/AopsCommonModalLayout'
import { userState } from '../../../store/user'

const { Text } = Typography

const StandaloneFastPopup = ({ id, open, onClose, onSuccess }) => {
  const [user] = useRecoilState(userState)
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    date: null,
    hour: '17',
    minute: '30',
    completed: false,
  })

  const isEditMode = !!id

  const hours = Array.from({ length: 24 }, (_, idx) =>
    String(idx).padStart(2, '0'),
  )
  const minutes = Array.from({ length: 6 }, (_, idx) =>
    String(idx * 10).padStart(2, '0'),
  )

  const resetForm = () => {
    setFormData({
      title: '',
      date: null,
      hour: '17',
      minute: '30',
      completed: false,
    })
    onClose()
  }

  // 상세 정보 조회
  const fetchFastlaneDetail = async () => {
    if (!id) return

    setIsLoading(true)
    try {
      const res = await getStandaloneFastlaneTask(id)
      if (res.status === 200) {
        const data = res.data
        const endDateTime = dayjs(data.endDateTime)
        setFormData({
          title: data.title || '',
          date: endDateTime,
          hour: endDateTime.format('HH'),
          minute: endDateTime.format('mm'),
          completed: data.completed || false,
        })
      } else {
        message.error('업무 상세 조회에 실패했습니다.')
      }
    } catch (error) {
      console.error('fetchFastlaneDetail error::', error)
      message.error('업무 상세 조회에 실패했습니다.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (open) {
      if (id) {
        fetchFastlaneDetail()
      } else {
        setFormData({
          title: '',
          date: null,
          hour: '17',
          minute: '30',
          completed: false,
        })
      }
    }
  }, [open, id])

  const validateForm = () => {
    if (!formData.title) return false
    if (!formData.date) return false
    if (!formData.hour) return false
    if (!formData.minute) return false
    return true
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      message.error('모든 필드를 입력해주세요')
      return
    }

    const endDateTimeString =
      formData.date.format('YYYY-MM-DD') +
      ' ' +
      formData.hour +
      ':' +
      formData.minute +
      ':00'

    try {
      const param = {
        salusUserId: user.id,
        title: formData.title,
        endDateTime: endDateTimeString,
      }

      if (isEditMode) {
        // 수정 모드
        const res = await updateStandaloneFastlaneTask(id, param)
        if (res.status === 200) {
          message.success('Fast 업무가 수정되었습니다.')
          onSuccess?.()
          resetForm()
        } else {
          message.error('Fast 업무 수정에 실패했습니다.')
        }
      } else {
        // 생성 모드
        const res = await createStandaloneFastlaneTask(param)
        if (res.status === 200) {
          message.success('Fast 업무가 생성되었습니다.')
          onSuccess?.()
          resetForm()
        } else {
          message.error('Fast 업무 생성에 실패했습니다.')
        }
      }
    } catch (error) {
      console.error('error::', error)
      message.error(
        `Fast 업무 ${isEditMode ? '수정' : '생성'}에 실패했습니다. 잠시 후 다시 시도해주세요.`,
      )
    }
  }

  const handleDelete = async () => {
    if (!id) return

    try {
      const res = await deleteStandaloneFastlaneTask(id)
      if (res.status === 200) {
        message.success('Fast 업무가 삭제되었습니다.')
        onSuccess?.()
        resetForm()
      } else {
        message.error('Fast 업무 삭제에 실패했습니다.')
      }
    } catch (error) {
      console.error('handleDelete error::', error)
      message.error('Fast 업무 삭제에 실패했습니다.')
    }
  }

  const handleComplete = async () => {
    if (!id || !user?.id) return

    try {
      if (formData.completed) {
        // 미완료로 변경
        const res = await incompleteStandaloneFastlane(id, user.id)
        if (res.status === 200) {
          message.success('Fast 업무가 미완료로 변경되었습니다.')
          setFormData((prev) => ({ ...prev, completed: false }))
          onSuccess?.()
          onClose()
        }
      } else {
        // 완료로 변경
        const res = await completeStandaloneFastlane(id, user.id)
        if (res.status === 200) {
          message.success('Fast 업무가 완료되었습니다.')
          setFormData((prev) => ({ ...prev, completed: true }))
          onSuccess?.()
          onClose()
        }
      }
    } catch (error) {
      console.error('handleComplete error::', error)
      message.error('업무 상태 변경에 실패했습니다.')
    }
  }

  return (
    <AopsCommonModalLayout
      open={open}
      onClose={onClose}
      title={isEditMode ? 'Fast 업무 상세' : 'Fast 업무 생성하기'}
    >
      <Spin spinning={isLoading}>
        <div className="form-container">
          <div className="form-wrapper">
            <div className="form-section">
              <label className="form-label">
                업무 명 <span style={{ color: 'red' }}>*</span>
              </label>
              <div style={{ width: '100%' }}>
                <Input
                  placeholder="생성할 체크리스트 업무의 이름을 입력해주세요"
                  value={formData.title}
                  onChange={(e) => {
                    if (e.target.value.length > 50) return
                    setFormData({ ...formData, title: e.target.value })
                  }}
                  className="form-input"
                  disabled={isEditMode && formData.completed}
                />
                {!isEditMode && (
                  <div className="example-text-group">
                    <Text className="example-text">패스트 업무란?</Text>
                    <Text
                      className="example-text"
                      style={{
                        fontSize: '12px',
                        color: '#585F65',
                        fontWeight: '400',
                      }}
                    >
                      그때그때 빠르게 처리해야 하는 단순·운영성 업무를 놓치지
                      않기 위한 업무 유형입니다
                    </Text>
                  </div>
                )}
              </div>
            </div>
            <div className="form-section">
              <label className="form-label">
                완료 시간 <span style={{ color: 'red' }}>*</span>
              </label>
              <div style={{ width: '100%' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: '100%',
                  }}
                >
                  <DatePicker
                    disabledDate={(current) =>
                      current && current < dayjs().startOf('day')
                    }
                    style={{ flex: 0.4, height: '48px' }}
                    placeholder="날짜를 선택해주세요"
                    suffixIcon={<CalendarFilled />}
                    value={formData.date}
                    onChange={(date) => setFormData({ ...formData, date })}
                    readOnly={true}
                    format="YYYY-MM-DD"
                    disabled={isEditMode && formData.completed}
                  />
                  <Select
                    style={{ width: 80, height: '48px' }}
                    value={formData.hour}
                    onChange={(hour) => setFormData({ ...formData, hour })}
                    options={hours.map((hour) => ({
                      label: hour,
                      value: hour,
                    }))}
                    popupMatchSelectWidth={false}
                    placeholder="Ex) 09"
                    disabled={isEditMode && formData.completed}
                  />
                  <span>:</span>
                  <Select
                    style={{ width: 80, height: '48px' }}
                    value={formData.minute}
                    onChange={(minute) => setFormData({ ...formData, minute })}
                    options={minutes.map((minute) => ({
                      label: minute,
                      value: minute,
                    }))}
                    popupMatchSelectWidth={false}
                    placeholder="Ex) 30"
                    disabled={isEditMode && formData.completed}
                  />
                </div>
              </div>
            </div>
          </div>
          {isEditMode ? (
            <div className="popup-footer-split">
              <button
                className="btn-submit btn-complete-center"
                onClick={handleComplete}
              >
                {formData.completed ? '완료됨' : '업무 완료'}
              </button>
              <div className="btn-group-right">
                <button className="btn-cancel" onClick={handleDelete}>
                  삭제
                </button>
                <button
                  className="btn-submit"
                  onClick={handleSubmit}
                  disabled={formData.completed}
                >
                  수정
                </button>
              </div>
            </div>
          ) : (
            <div className="popup-footer">
              <button className="btn-cancel" onClick={resetForm}>
                취소
              </button>
              <button className="btn-submit" onClick={handleSubmit}>
                등록
              </button>
            </div>
          )}
        </div>
      </Spin>
    </AopsCommonModalLayout>
  )
}

export default StandaloneFastPopup
